import { asGroupedFieldOptionsForFilter } from 'module/helpers/filter/grouped-field-transformer';
import { useTranslate } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { keyBy } from 'lodash';
import { useMemo } from 'react';

export const useFields = (): {
  fields: GroupedOptions<FilterableField>[];
  lookups: GroupFieldsMeta;
  isLoading: boolean;
} => {
  const { fields: userFields, loading: isUserFieldsLoading } =
    useExternalTeamMemberFieldsAsFilterableField();
  const userFieldsLookup = useMemo(
    () => keyBy(userFields, 'fieldName'),
    [userFields],
  );
  const { fields: companyFields, loading: isCompanyFieldsLoading } =
    useCompanyFieldsAsFilterableField();
  const companyFieldsLookup = useMemo(() => {
    return keyBy(companyFields, 'fieldName');
  }, [companyFields]);
  const translate = useTranslate();
  const fields = useMemo(
    () =>
      asGroupedFieldOptionsForFilter(
        {
          [FilterGroupType.User]: {
            fields: userFields,
          },
          [FilterGroupType.Company]: {
            fields: companyFields,
          },
        },
        translate,
      ),
    [userFields, companyFields, translate],
  );
  const lookups: GroupFieldsMeta = useMemo(() => {
    return [
      {
        id: FilterGroupType.User,
        fields: userFieldsLookup,
      },
      {
        id: FilterGroupType.Company,
        fields: companyFieldsLookup,
      },
    ];
  }, [userFieldsLookup, companyFieldsLookup]);

  return useMemo(
    () => ({
      isLoading: isUserFieldsLoading || isCompanyFieldsLoading,
      fields,
      lookups,
    }),
    [isUserFieldsLoading, isCompanyFieldsLoading, fields, lookups],
  );
};
